import React, { useState } from "react";

import { HubAccessLevel } from "@gamedev-web/one-gaming-crm";
import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { Close } from "@gaming/xds-react-js-icons";

import { Modal, ModalInitialFocus } from "../../../../components/Modal/Modal";
import { useStrings } from "../../../../localization/localizationHelpers";
import { AdminInvitationConflict } from "../../../../models/adminPage";
import type { AdminInvitationInput } from "../../../../models/adminPage";
import { InvitationForm } from "./InvitationForm";
import * as styles from "./InviteContactModal.module.css";

type InviteContactModalProps = Readonly<{
  initialInvitation?: AdminInvitationInput;
  isEmailReadOnly?: boolean;
  isOpen: boolean;
  isResend?: boolean;
  onClose: () => void;
  onSubmit: (invitation: AdminInvitationInput) => Promise<AdminInvitationConflict | null>;
}>;

const emptyInvitation: AdminInvitationInput = {
  firstName: "",
  lastName: "",
  jobTitle: "",
  email: "",
  accessLevel: HubAccessLevel.User,
};

export const InviteContactModal = ({
  initialInvitation = emptyInvitation,
  isEmailReadOnly = false,
  isOpen,
  isResend = false,
  onClose,
  onSubmit,
}: InviteContactModalProps) => {
  const [conflict, setConflict] = useState<AdminInvitationConflict | null>(null);
  const [
    title,
    description,
    sendInvitationLabel,
    cancelLabel,
    closeLabel,
    existingContactTitle,
    existingContactMessage,
    existingInvitationTitle,
    existingInvitationMessage,
    backLabel,
    resendTitle,
    resendDescription,
    resendInvitationLabel,
  ] = useStrings(
    "partnerAdminInviteTitle",
    "partnerAdminInviteDescription",
    "partnerAdminSendInvitationLabel",
    "partnerAdminInviteCancelLabel",
    "partnerAdminInviteCloseLabel",
    "partnerAdminInviteExistingContactTitle",
    "partnerAdminInviteExistingContactMessage",
    "partnerAdminInviteExistingInvitationTitle",
    "partnerAdminInviteExistingInvitationMessage",
    "back",
    "partnerAdminResendInvitationTitle",
    "partnerAdminResendInvitationDescription",
    "partnerAdminResendInvitationLabel"
  );
  const close = () => {
    setConflict(null);
    onClose();
  };
  const isExistingContact = conflict === AdminInvitationConflict.ExistingContact;
  const conflictTitle = isExistingContact ? existingContactTitle : existingInvitationTitle;
  const conflictMessage = isExistingContact ? existingContactMessage : existingInvitationMessage;

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      ariaLabelledBy={
        conflict ? "partner-admin-invite-conflict-title" : "partner-admin-invite-title"
      }
      initialFocus={ModalInitialFocus.FirstInput}
    >
      <div hidden={Boolean(conflict)}>
        <InvitationForm
          resetKey={isOpen}
          initialInvitation={initialInvitation}
          title={isResend ? resendTitle : title}
          titleId="partner-admin-invite-title"
          description={isResend ? resendDescription : description}
          submitLabel={isResend ? resendInvitationLabel : sendInvitationLabel}
          cancelLabel={cancelLabel}
          closeLabel={closeLabel}
          isEmailReadOnly={isEmailReadOnly}
          onCancel={close}
          onSubmit={async invitation => {
            const result = await onSubmit(invitation);
            if (result) {
              setConflict(result);
              return;
            }
            close();
          }}
        />
      </div>
      {conflict && (
        <div className={styles.form}>
          <div className={styles.header}>
            <div>
              <h2 id="partner-admin-invite-conflict-title" className={styles.title}>
                {conflictTitle}
              </h2>
              <p className={styles.description}>{conflictMessage}</p>
            </div>
            <button type="button" className={styles.close} aria-label={closeLabel} onClick={close}>
              <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
            </button>
          </div>
          <div className={styles.actions}>
            <BasicButton
              as="button"
              buttonType={ButtonType.Brand}
              type="button"
              onClick={() => setConflict(null)}
            >
              {backLabel}
            </BasicButton>
          </div>
        </div>
      )}
    </Modal>
  );
};
